import { Message } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  crmOpportunityWorkbenchDefaultListParams,
  crmOpportunityWorkbenchQueryRootKey,
} from "../config";
import {
  createOpportunity,
  createOpportunityFollowupRecord,
  deleteOpportunityFollowupRecord,
  disableOpportunity,
  enableOpportunity,
  fetchOpportunityCustomizationProjects,
  fetchOpportunityFollowupRecords,
  fetchOpportunityList,
  fetchOpportunityMetadata,
  fetchOpportunitySignInfo,
  fetchOpportunityStageBoard,
  fetchOpportunityStageOwners,
  saveOpportunityManualStage,
  saveOpportunitySignInfo,
  saveOpportunityStageOwners,
  searchOpportunityContacts,
  searchOpportunityCustomers,
  searchOpportunityOrganizations,
  updateOpportunity,
} from "../repo/crmOpportunityWorkbenchRepo";
import { normalizeOpportunityWorkbenchFilters, normalizeSignInfoStatusFilters } from "../service";
import type {
  OpportunityCreatePayload,
  OpportunityFollowupCreatePayload,
  OpportunityFollowupListPage,
  OpportunitySignInfoSavePayload,
  OpportunityStageOwnerBulkAssignPayload,
  OpportunityManualStagePayload,
  OpportunityWorkbenchItem,
  OpportunityWorkbenchListFilters,
  OpportunityWorkbenchListParams,
} from "../types";

const OPPORTUNITY_WORKBENCH_PAGE_SIZE_OPTIONS = new Set([5, 10, 20, 50, 100]);
/** 窄屏商机列表默认每页条数；桌面仍用配置默认 20。 */
export const OPPORTUNITY_WORKBENCH_MOBILE_PAGE_SIZE = 5;
const OPPORTUNITY_WORKBENCH_SORT_FIELDS = new Set([
  "updated_at",
  "created_at",
  "last_followup_at",
  "travel_month",
  "opportunity_code",
  "opportunity_name",
]);

type OpportunityWorkbenchUrlState = {
  filters: OpportunityWorkbenchListFilters;
  page: number;
  pageSize: number;
};

/** 编排商机工作台筛选、分页、抽屉和变更操作。 */
export function useCrmOpportunityWorkbenchRuntime() {
  const queryClient = useQueryClient();
  const initialUrlState = useMemo(readOpportunityWorkbenchUrlState, []);
  const [draftFilters, setDraftFilters] = useState<OpportunityWorkbenchListFilters>(
    () => initialUrlState.filters,
  );
  const [submittedFilters, setSubmittedFilters] = useState<OpportunityWorkbenchListFilters>(
    () => initialUrlState.filters,
  );
  const [page, setPage] = useState(initialUrlState.page);
  const [pageSize, setPageSize] = useState(initialUrlState.pageSize);
  const [selectedFollowupOpportunity, setSelectedFollowupOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [selectedSignInfoOpportunity, setSelectedSignInfoOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [selectedStageOpportunity, setSelectedStageOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [selectedStageOwnerOpportunity, setSelectedStageOwnerOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [selectedEditOpportunity, setSelectedEditOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
  const [followupPage, setFollowupPage] = useState(1);
  const [followupDrawerTab, setFollowupDrawerActiveTab] = useState<"followups" | "traces">(
    "followups",
  );

  const listParams = useMemo(
    () => ({
      ...crmOpportunityWorkbenchDefaultListParams,
      ...submittedFilters,
      sortField: submittedFilters.sortField ?? crmOpportunityWorkbenchDefaultListParams.sortField,
      sortDirection:
        submittedFilters.sortDirection ?? crmOpportunityWorkbenchDefaultListParams.sortDirection,
      page,
      pageSize,
    }),
    [page, pageSize, submittedFilters],
  );

  useEffect(() => {
    writeOpportunityWorkbenchUrlState(listParams);
  }, [listParams]);

  const metadataQuery = useQuery({
    queryKey: [...crmOpportunityWorkbenchQueryRootKey, "metadata"],
    queryFn: fetchOpportunityMetadata,
  });

  const listQuery = useQuery({
    queryKey: [...crmOpportunityWorkbenchQueryRootKey, "opportunities", listParams],
    queryFn: () => fetchOpportunityList(listParams),
  });

  const followupQuery = useQuery({
    queryKey: [
      ...crmOpportunityWorkbenchQueryRootKey,
      "followups",
      selectedFollowupOpportunity?.id,
      followupPage,
    ],
    queryFn: () =>
      fetchOpportunityFollowupRecords({
        opportunityId: selectedFollowupOpportunity?.id as number,
        page: followupPage,
        pageSize: 20,
      }),
    enabled: Boolean(selectedFollowupOpportunity),
  });

  const signInfoQuery = useQuery({
    queryKey: [
      ...crmOpportunityWorkbenchQueryRootKey,
      "sign-info",
      selectedSignInfoOpportunity?.id,
    ],
    queryFn: () => fetchOpportunitySignInfo(selectedSignInfoOpportunity?.id as number),
    enabled: Boolean(selectedSignInfoOpportunity),
  });

  const customizationProjectsQuery = useQuery({
    queryKey: [
      ...crmOpportunityWorkbenchQueryRootKey,
      "customization-projects",
      selectedFollowupOpportunity?.id,
    ],
    queryFn: () =>
      fetchOpportunityCustomizationProjects(selectedFollowupOpportunity?.id as number),
    enabled: Boolean(selectedFollowupOpportunity) && followupDrawerTab === "traces",
  });

  const stageBoardQuery = useQuery({
    queryKey: [...crmOpportunityWorkbenchQueryRootKey, "stage-board", selectedStageOpportunity?.id],
    queryFn: () =>
      fetchOpportunityStageBoard
        ? fetchOpportunityStageBoard(selectedStageOpportunity?.id as number)
        : Promise.resolve(undefined),
    enabled: Boolean(selectedStageOpportunity),
  });

  const stageOwnersQuery = useQuery({
    queryKey: [
      ...crmOpportunityWorkbenchQueryRootKey,
      "stage-owners",
      selectedStageOwnerOpportunity?.id,
    ],
    queryFn: () => fetchOpportunityStageOwners(selectedStageOwnerOpportunity?.id as number),
    enabled: Boolean(selectedStageOwnerOpportunity),
  });

  const createOpportunityMutation = useMutation({
    mutationFn: createOpportunity,
    onSuccess: async () => {
      Message.success("商机已创建");
      setCreateDrawerVisible(false);
      await queryClient.invalidateQueries({ queryKey: crmOpportunityWorkbenchQueryRootKey });
    },
    onError: () => Message.error("商机创建失败，请稍后重试"),
  });

  const updateOpportunityMutation = useMutation({
    mutationFn: updateOpportunity,
    onSuccess: async () => {
      Message.success("商机已修改");
      setSelectedEditOpportunity(null);
      await queryClient.invalidateQueries({ queryKey: crmOpportunityWorkbenchQueryRootKey });
    },
    onError: (error) => Message.error(resolveOpportunityMutationError(error, "商机修改失败，请稍后重试")),
  });

  const createFollowupMutation = useMutation({
    mutationFn: createOpportunityFollowupRecord,
    onSuccess: async (record, variables) => {
      Message.success("跟进记录已保存");
      setFollowupPage(1);
      queryClient.setQueryData<OpportunityFollowupListPage>(
        [...crmOpportunityWorkbenchQueryRootKey, "followups", variables.opportunityId, 1],
        (current) => {
          if (!current) {
            return current;
          }
          const nextItems = [
            record,
            ...current.items.filter((item) => item.id !== record.id),
          ].slice(0, current.pageSize);
          return {
            ...current,
            items: nextItems,
            total: current.total + (current.items.some((item) => item.id === record.id) ? 0 : 1),
          };
        },
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...crmOpportunityWorkbenchQueryRootKey, "followups", variables.opportunityId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmOpportunityWorkbenchQueryRootKey, "opportunities"],
        }),
      ]);
    },
    onError: () => Message.error("跟进记录保存失败，请稍后重试"),
  });

  const deleteFollowupMutation = useMutation({
    mutationFn: deleteOpportunityFollowupRecord,
    onSuccess: async () => {
      Message.success("跟进记录已删除");
      await queryClient.invalidateQueries({ queryKey: crmOpportunityWorkbenchQueryRootKey });
    },
    onError: () => Message.error("跟进记录删除失败，请稍后重试"),
  });

  const disableOpportunityMutation = useMutation({
    mutationFn: disableOpportunity,
    onSuccess: async () => {
      Message.success("商机已停用");
      await queryClient.invalidateQueries({ queryKey: crmOpportunityWorkbenchQueryRootKey });
    },
    onError: () => Message.error("商机停用失败，请稍后重试"),
  });

  const enableOpportunityMutation = useMutation({
    mutationFn: enableOpportunity,
    onSuccess: async () => {
      Message.success("商机已启用");
      await queryClient.invalidateQueries({ queryKey: crmOpportunityWorkbenchQueryRootKey });
    },
    onError: () => Message.error("商机启用失败，请稍后重试"),
  });

  const saveStageOwnersMutation = useMutation({
    mutationFn: saveOpportunityStageOwners,
    onSuccess: async () => {
      Message.success("阶段负责人设置已保存");
      setSelectedStageOwnerOpportunity(null);
      await queryClient.invalidateQueries({ queryKey: crmOpportunityWorkbenchQueryRootKey });
    },
    onError: () => Message.error("阶段负责人设置保存失败，请稍后重试"),
  });

  const saveManualStageMutation = useMutation({
    // 兼容旧版按需 mock/构建产物；正式 repo 始终提供该函数。
    mutationFn:
      saveOpportunityManualStage ??
      (async () => {
        throw new Error("人工阶段接口不可用");
      }),
    onSuccess: async (board) => {
      Message.success("阶段设置已保存");
      queryClient.setQueryData(
        [...crmOpportunityWorkbenchQueryRootKey, "stage-board", board.opportunityId],
        board,
      );
      await queryClient.invalidateQueries({
        queryKey: [...crmOpportunityWorkbenchQueryRootKey, "opportunities"],
      });
    },
    onError: (error) =>
      Message.error(resolveOpportunityMutationError(error, "阶段设置保存失败，请稍后重试")),
  });

  const saveSignInfoMutation = useMutation({
    mutationFn: saveOpportunitySignInfo,
    onSuccess: async () => {
      Message.success("商机进度已保存");
      setSelectedSignInfoOpportunity(null);
      await queryClient.invalidateQueries({
        queryKey: [...crmOpportunityWorkbenchQueryRootKey, "opportunities"],
      });
    },
    onError: (error) =>
      Message.error(resolveOpportunityMutationError(error, "商机进度保存失败，请稍后重试")),
  });

  const listPage = listQuery.data ?? {
    items: [],
    total: 0,
    page,
    pageSize,
  };

  return {
    draftFilters,
    setDraftFilters,
    submittedFilters,
    metadata: metadataQuery.data,
    items: listPage.items,
    total: listPage.total,
    page,
    pageSize,
    selectedFollowupOpportunity,
    selectedSignInfoOpportunity,
    sortField: listParams.sortField,
    sortDirection: listParams.sortDirection,
    selectedStageOpportunity,
    selectedStageOwnerOpportunity,
    selectedEditOpportunity,
    createDrawerVisible,
    followupPage: followupQuery.data,
    signInfoDetail: signInfoQuery.data,
    customizationProjects: customizationProjectsQuery.data,
    stageBoard: stageBoardQuery.data,
    stageOwners: stageOwnersQuery.data,
    loading: listQuery.isLoading || metadataQuery.isLoading,
    followupLoading: followupQuery.isLoading,
    signInfoLoading: signInfoQuery.isLoading,
    customizationProjectsLoading: customizationProjectsQuery.isLoading,
    stageBoardLoading: stageBoardQuery.isLoading,
    stageOwnerLoading: stageOwnersQuery.isLoading,
    mutating:
      createOpportunityMutation.isPending ||
      updateOpportunityMutation.isPending ||
      createFollowupMutation.isPending ||
      deleteFollowupMutation.isPending ||
      disableOpportunityMutation.isPending ||
      enableOpportunityMutation.isPending ||
      saveStageOwnersMutation.isPending ||
      saveManualStageMutation.isPending ||
      saveSignInfoMutation.isPending,
    handleSearch(nextFilters?: OpportunityWorkbenchListFilters) {
      const source = nextFilters ?? draftFilters;
      if (nextFilters) {
        setDraftFilters(nextFilters);
      }
      setSubmittedFilters(normalizeOpportunityWorkbenchFilters(source));
      setPage(1);
    },
    /** 刷新当前列表查询，不改变用户正在查看的页码。 */
    refreshList() {
      return queryClient.invalidateQueries({
        queryKey: [...crmOpportunityWorkbenchQueryRootKey, "opportunities"],
      });
    },
    handleReset() {
      setDraftFilters({});
      setSubmittedFilters({});
      setPage(1);
    },
    handleSort(sortField: string, sortDirection: "asc" | "desc") {
      const nextSort = { sortField, sortDirection };
      setDraftFilters((current) => ({ ...current, ...nextSort }));
      setSubmittedFilters((current) => ({ ...current, ...nextSort }));
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    openCreateDrawer() {
      setCreateDrawerVisible(true);
    },
    closeCreateDrawer() {
      setCreateDrawerVisible(false);
    },
    openEditDrawer(opportunity: OpportunityWorkbenchItem) {
      setSelectedEditOpportunity(opportunity);
    },
    closeEditDrawer() {
      setSelectedEditOpportunity(null);
    },
    openFollowupDrawer(opportunity: OpportunityWorkbenchItem) {
      setSelectedFollowupOpportunity(opportunity);
      setFollowupPage(1);
      setFollowupDrawerActiveTab("followups");
    },
    closeFollowupDrawer() {
      setSelectedFollowupOpportunity(null);
      setFollowupPage(1);
      setFollowupDrawerActiveTab("followups");
    },
    openSignInfoDrawer(opportunity: OpportunityWorkbenchItem) {
      setSelectedSignInfoOpportunity(opportunity);
    },
    closeSignInfoDrawer() {
      setSelectedSignInfoOpportunity(null);
    },
    setFollowupDrawerTab(tab: "followups" | "traces") {
      setFollowupDrawerActiveTab(tab);
    },
    openStageDrawer(opportunity: OpportunityWorkbenchItem) {
      setSelectedStageOpportunity(opportunity);
    },
    closeStageDrawer() {
      setSelectedStageOpportunity(null);
    },
    openStageOwnerDrawer(opportunity: OpportunityWorkbenchItem) {
      setSelectedStageOwnerOpportunity(opportunity);
    },
    closeStageOwnerDrawer() {
      setSelectedStageOwnerOpportunity(null);
    },
    handleFollowupPageChange(nextPage: number) {
      setFollowupPage(nextPage);
    },
    searchOrganizations(keyword: string) {
      return searchOpportunityOrganizations({ keyword, limit: 20 });
    },
    searchCustomers(orgId: number, keyword: string) {
      return searchOpportunityCustomers({ orgId, keyword, limit: 20 });
    },
    searchContacts(orgId: number, customerId: number | undefined, keyword: string) {
      return searchOpportunityContacts({ orgId, customerId, keyword, limit: 20 });
    },
    async createOpportunity(payload: OpportunityCreatePayload) {
      await createOpportunityMutation.mutateAsync(payload);
    },
    async updateOpportunity(opportunityId: number, payload: OpportunityCreatePayload) {
      await updateOpportunityMutation.mutateAsync({ opportunityId, payload });
    },
    async createFollowup(opportunityId: number, payload: OpportunityFollowupCreatePayload) {
      await createFollowupMutation.mutateAsync({ opportunityId, payload });
    },
    async deleteFollowup(opportunityId: number, recordId: number) {
      await deleteFollowupMutation.mutateAsync({ opportunityId, recordId });
    },
    async saveSignInfo(opportunityId: number, payload: OpportunitySignInfoSavePayload) {
      await saveSignInfoMutation.mutateAsync({ opportunityId, payload });
    },
    async disableOpportunity(opportunityId: number) {
      await disableOpportunityMutation.mutateAsync(opportunityId);
    },
    async enableOpportunity(opportunityId: number) {
      await enableOpportunityMutation.mutateAsync(opportunityId);
    },
    async saveStageOwners(opportunityId: number, payload: OpportunityStageOwnerBulkAssignPayload) {
      await saveStageOwnersMutation.mutateAsync({ opportunityId, payload });
    },
    async saveManualStage(opportunityId: number, payload: OpportunityManualStagePayload) {
      await saveManualStageMutation.mutateAsync({ opportunityId, payload });
    },
  };
}

/** 优先展示服务端归一后的业务详情，避免把名称冲突等可处理问题隐藏成通用失败。 */
function resolveOpportunityMutationError(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

/** 从当前 URL 读取商机工作台可恢复的查询状态。 */
function readOpportunityWorkbenchUrlState(): OpportunityWorkbenchUrlState {
  const defaults = {
    filters: normalizeOpportunityWorkbenchFilters({}),
    page: crmOpportunityWorkbenchDefaultListParams.page,
    pageSize: crmOpportunityWorkbenchDefaultListParams.pageSize,
  };
  if (typeof window === "undefined") {
    return defaults;
  }

  const params = new URLSearchParams(window.location.search);
  const page = parsePositiveInteger(params.get("page"));
  const pageSize = parsePositiveInteger(params.get("pageSize"));
  const sortField = params.get("sort");
  const sortDirection = params.get("direction");
  const hasSelected = params.get("hasSelectedCustomization");
  const stages = readUrlTexts(params, "stages", "stage");
  const filters = normalizeOpportunityWorkbenchFilters({
    keyword: readOptionalUrlText(params, "keyword"),
    customizationKeyword: readOptionalUrlText(params, "customizationKeyword"),
    orgId: parsePositiveInteger(params.get("orgId")),
    orgName: readOptionalUrlText(params, "orgName"),
    customerKeyword: readOptionalUrlText(params, "customerKeyword"),
    travelMonth: readOptionalUrlText(params, "travelMonth"),
    stages,
    status: readOptionalUrlText(params, "status"),
    signInfoStatuses: normalizeSignInfoStatusFilters(
      readUrlTexts(params, "signInfoStatuses", "signInfoStatus"),
    ),
    ownerUserId: parsePositiveInteger(params.get("ownerUserId")),
    hasSelectedCustomization:
      hasSelected === "true" ? true : hasSelected === "false" ? false : undefined,
    sortField:
      sortField && OPPORTUNITY_WORKBENCH_SORT_FIELDS.has(sortField) ? sortField : undefined,
    sortDirection: sortDirection === "asc" || sortDirection === "desc" ? sortDirection : undefined,
  });

  return {
    filters,
    page: page ?? defaults.page,
    pageSize:
      pageSize && OPPORTUNITY_WORKBENCH_PAGE_SIZE_OPTIONS.has(pageSize)
        ? pageSize
        : defaults.pageSize,
  };
}

/** 把商机工作台查询状态写入 URL，便于切换标签后恢复筛选。 */
function writeOpportunityWorkbenchUrlState(params: OpportunityWorkbenchListParams) {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  setUrlText(url.searchParams, "page", String(params.page));
  setUrlText(url.searchParams, "pageSize", String(params.pageSize));
  setUrlText(url.searchParams, "keyword", params.keyword);
  setUrlText(url.searchParams, "customizationKeyword", params.customizationKeyword);
  setUrlText(url.searchParams, "orgId", params.orgId?.toString());
  setUrlText(url.searchParams, "orgName", params.orgName);
  setUrlText(url.searchParams, "customerKeyword", params.customerKeyword);
  setUrlText(url.searchParams, "travelMonth", params.travelMonth);
  setUrlText(url.searchParams, "status", params.status);
  setUrlTexts(url.searchParams, "signInfoStatuses", params.signInfoStatuses);
  url.searchParams.delete("signInfoStatus");
  setUrlText(url.searchParams, "ownerUserId", params.ownerUserId?.toString());
  setUrlText(
    url.searchParams,
    "hasSelectedCustomization",
    params.hasSelectedCustomization === undefined
      ? undefined
      : params.hasSelectedCustomization
        ? "true"
        : "false",
  );
  setUrlTexts(url.searchParams, "stages", params.stages);
  url.searchParams.delete("stage");
  setUrlText(url.searchParams, "sort", params.sortField);
  setUrlText(url.searchParams, "direction", params.sortDirection);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}`);
}

/** 解析正整数 URL 参数。 */
function parsePositiveInteger(value: string | null) {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/** 读取可选的非空 URL 文本参数。 */
function readOptionalUrlText(params: URLSearchParams, key: string) {
  const value = params.get(key)?.trim();
  return value || undefined;
}

/** 读取多值 URL 文本参数，并兼容旧单值键。 */
function readUrlTexts(params: URLSearchParams, key: string, legacyKey: string) {
  const values = params.getAll(key);
  const legacyValue = readOptionalUrlText(params, legacyKey);
  if (legacyValue) values.push(legacyValue);
  const normalized = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
  return normalized.length ? normalized : undefined;
}

/** 写入或删除单个 URL 文本参数。 */
function setUrlText(params: URLSearchParams, key: string, value?: string) {
  if (value) {
    params.set(key, value);
    return;
  }
  params.delete(key);
}

/** 写入多值 URL 文本参数。 */
function setUrlTexts(params: URLSearchParams, key: string, values?: string[]) {
  params.delete(key);
  values?.forEach((value) => params.append(key, value));
}
